import type { TConversationBootstrapResponse } from "../types/agent";
import type { TTapeSearchParameters } from "../types/tapes.type";
import type { AxiosRequestConfig } from "axios";

import { getEndpoint } from "../endpoints";
import {
	LitheCreationTypeEnum,
	type TLitheCreationData,
	type TLitheEngineeringFeature,
	type TLitheEngineeringFeaturePreviewRequest,
	type TLitheEngineeringFeaturePreviewResponse,
	type TLitheEngineeringFeaturesResponse,
	type TLitheUgFeatureEngineeringResponse,
	type TLitheQuestionApiResponse,
	type TLitheQuestionRequestObject,
	type TLitheQuestionsStepPath,
	type TLitheSearchResponseProps,
} from "../types/lithe.type";

import {
	httpServiceInstance,
	type HttpService,
} from "./http.service";

const isRecord = (
	value: unknown
): value is Record<string, any> => {
	return typeof value === "object" && value !== null;
};

type TLitheSourceColumnsResponse = {
	source_col_names?: Array<unknown>;
	source_columns?: Array<unknown>;
};

export class LitheService {
	private static http: HttpService;
	private static endpoint: string;
	private static base_url: string;

	constructor() {
		LitheService.http = httpServiceInstance;
		LitheService.endpoint = getEndpoint();
		LitheService.base_url = `${LitheService.endpoint}/api/lithes`;
	}

	private buildQuestionPayload(
		requestObject: TLitheQuestionRequestObject
	): Record<string, any> {
		const payload: Record<string, any> = {
			question_id: requestObject.questionId,
			question_category_id:
				requestObject.questionCategoryId,
			user_input: requestObject.userInput,
			user_input_type: requestObject.userInputType,
		};

		if (requestObject.answerFormat) {
			payload.answer_format = requestObject.answerFormat;
		}

		return payload;
	}

	private logApiResponse<T>(label: string, response: T): T {
		// eslint-disable-next-line no-console
		console.log(
			`[LitheService] ${label} response`,
			response
		);
		return response;
	}

	private async getStepQuestions(
		litheId: string,
		stepPath: string
	): Promise<Array<TLitheQuestionApiResponse>> {
		const url = `${LitheService.base_url}/${litheId}/${stepPath}/questions`;
		const { data } =
			await LitheService.http.get<
				Array<TLitheQuestionApiResponse>
			>(url);
		return this.logApiResponse(
			`GET /${stepPath}/questions`,
			data
		);
	}

	async getLitheStepQuestions(
		litheId: string,
		stepPath: TLitheQuestionsStepPath
	): Promise<Array<TLitheQuestionApiResponse>> {
		return await this.getStepQuestions(litheId, stepPath);
	}

	private async updateStepQuestion(
		stepPath: string,
		requestObject: TLitheQuestionRequestObject
	): Promise<any> {
		const url = `${LitheService.base_url}/${requestObject.litheId}/${stepPath}/questions`;
		const { data } = await LitheService.http.post<any>(
			url,
			{
				data: this.buildQuestionPayload(requestObject),
			}
		);
		return this.logApiResponse(
			`POST /${stepPath}/questions`,
			data
		);
	}

	private async getStepResult(
		litheId: string,
		stepPath: string
	): Promise<any> {
		const url = `${LitheService.base_url}/${litheId}/${stepPath}`;
		const { data } = await LitheService.http.get<any>(url);
		return this.logApiResponse(`GET /${stepPath}`, data);
	}

	private async postStepConfirmation(
		litheId: string,
		stepPath: string,
		isStepConfirmed: boolean
	): Promise<any> {
		const url = `${LitheService.base_url}/${litheId}/${stepPath}`;
		const { data } = await LitheService.http.post<any>(
			url,
			{
				data: {
					is_step_confirmed: isStepConfirmed,
				},
			}
		);
		return this.logApiResponse(`POST /${stepPath}`, data);
	}

	private async confirmStep(
		litheId: string,
		stepPath: string
	): Promise<any> {
		return await this.postStepConfirmation(
			litheId,
			stepPath,
			true
		);
	}

	async getLitheConversation(
		litheId: string,
		options?: {
			forceCreate?: boolean;
		}
	): Promise<TConversationBootstrapResponse> {
		const url = `${LitheService.base_url}/${litheId}/conversation`;
		const requestData: Record<string, boolean> = {};
		if (options?.forceCreate) {
			requestData.force_create = true;
		}

		const { data } =
			await LitheService.http.post<TConversationBootstrapResponse>(
				url,
				Object.keys(requestData).length > 0
					? {
							data: requestData,
						}
					: undefined
			);
		return this.logApiResponse("POST /conversation", data);
	}

	private normalizeCustomColumnsCollection(
		customColumns: unknown
	): Array<Record<string, any>> {
		if (Array.isArray(customColumns)) {
			return customColumns.map((column) =>
				isRecord(column) ? column : {}
			);
		}

		if (isRecord(customColumns)) {
			return Object.entries(customColumns).map(
				([colId, column]) => ({
					...(isRecord(column) ? column : {}),
					col_id:
						(isRecord(column)
							? (column.col_id ?? column.column)
							: undefined) ?? colId,
				})
			);
		}

		return [];
	}

	private normalizePreviewValue(
		value: unknown
	): string | number | boolean | null {
		if (value === null || value === undefined) {
			return null;
		}

		if (
			typeof value === "string" ||
			typeof value === "number" ||
			typeof value === "boolean"
		) {
			return value;
		}

		return JSON.stringify(value);
	}

	private buildPreviewRowsFromValues(
		previewValues: unknown,
		fallbackColumnName: string
	): Array<
		Record<string, string | number | boolean | null>
	> {
		if (Array.isArray(previewValues)) {
			if (previewValues.every((row) => isRecord(row))) {
				return previewValues.map((row) => {
					const normalizedRow: Record<
						string,
						string | number | boolean | null
					> = {};

					Object.entries(
						row as Record<string, unknown>
					).forEach(([columnName, value]) => {
						normalizedRow[columnName] =
							this.normalizePreviewValue(value);
					});

					return normalizedRow;
				});
			}

			return previewValues.map((value) => ({
				[fallbackColumnName]:
					this.normalizePreviewValue(value),
			}));
		}

		if (isRecord(previewValues)) {
			const columnEntries = Object.entries(
				previewValues
			).reduce<Array<[string, Array<unknown>]>>(
				(accumulator, [columnName, value]) => {
					if (Array.isArray(value)) {
						accumulator.push([
							columnName,
							value as Array<unknown>,
						]);
					}

					return accumulator;
				},
				[]
			);

			if (columnEntries.length === 0) {
				return Object.values(previewValues).map(
					(value) => ({
						[fallbackColumnName]:
							this.normalizePreviewValue(value),
					})
				);
			}

			const rowCount = columnEntries.reduce(
				(maxLength, [, values]) =>
					Math.max(maxLength, values.length),
				0
			);

			return Array.from(
				{ length: rowCount },
				(_, rowIndex) =>
					Object.fromEntries(
						columnEntries.map(([columnName, values]) => [
							columnName,
							this.normalizePreviewValue(values[rowIndex]),
						])
					)
			);
		}

		return [];
	}

	private getPreviewRowsFromFeatureState(
		feature: unknown,
		fallbackColumnName: string
	): Array<
		Record<string, string | number | boolean | null>
	> {
		const featureState = isRecord(feature) ? feature : {};

		if (Array.isArray(featureState.previewRows)) {
			return this.buildPreviewRowsFromValues(
				featureState.previewRows,
				fallbackColumnName
			);
		}

		if (Array.isArray(featureState.preview_rows)) {
			return this.buildPreviewRowsFromValues(
				featureState.preview_rows,
				fallbackColumnName
			);
		}

		const initialValues = isRecord(
			featureState.initial_values
		)
			? featureState.initial_values
			: {};
		const finalValues = isRecord(featureState.final_values)
			? featureState.final_values
			: {};

		return this.buildPreviewRowsFromValues(
			{
				...initialValues,
				...finalValues,
			},
			fallbackColumnName
		);
	}

	private normalizeEngineeringFeature(
		feature: any
	): TLitheEngineeringFeature {
		const metadata = isRecord(feature?.column_metadata)
			? feature.column_metadata
			: {};
		const featureName =
			feature.featureName ??
			feature.feature_name ??
			feature.name ??
			metadata.display_name ??
			metadata.header_name ??
			"";

		return {
			id:
				feature.id ??
				feature.col_id ??
				feature.column ??
				"",
			featureName,
			description:
				feature.description ?? metadata.description ?? "",
			formula: feature.formula ?? feature.user_input ?? "",
			datatype:
				feature.datatype ?? metadata.datatype ?? "str",
			formulaType: "sql",
			isDefault:
				feature.isDefault ?? feature.is_default ?? false,
			isApproved:
				feature.isApproved ??
				feature.is_approved ??
				feature.is_user_confirmed ??
				false,
			previewRows: this.getPreviewRowsFromFeatureState(
				feature,
				String(featureName || "value")
			),
		};
	}

	private getNormalizedEngineeringFeatures(
		response: any
	): Array<TLitheEngineeringFeature> {
		const featureSource =
			[
				response,
				isRecord(response?.output) ? response.output : null,
				isRecord(response?.result) ? response.result : null,
				isRecord(response?.data) ? response.data : null,
			].reduce<Array<any> | null>(
				(accumulator, candidate) => {
					if (
						accumulator !== null ||
						!isRecord(candidate)
					) {
						return accumulator;
					}

					if (Array.isArray(candidate.features)) {
						return candidate.features;
					}

					const normalizedCustomColumns =
						this.normalizeCustomColumnsCollection(
							candidate.custom_columns ??
								candidate.user_defined_cols ??
								candidate.columns
						);

					return normalizedCustomColumns.length > 0
						? normalizedCustomColumns
						: accumulator;
				},
				null
			) ?? [];

		return featureSource.map((feature: any) =>
			this.normalizeEngineeringFeature(feature)
		);
	}

	private findEngineeringFeatureInResponse(
		response: any,
		target: {
			featureId?: string | null;
			featureName?: string;
		}
	): TLitheEngineeringFeature | null {
		const normalizedFeatures =
			this.getNormalizedEngineeringFeatures(response);
		const normalizedTargetName = String(
			target.featureName ?? ""
		)
			.trim()
			.toLowerCase();

		return (
			normalizedFeatures.find(
				(feature) =>
					Boolean(target.featureId) &&
					feature.id === target.featureId
			) ??
			normalizedFeatures.find(
				(feature) =>
					normalizedTargetName.length > 0 &&
					feature.featureName.trim().toLowerCase() ===
						normalizedTargetName
			) ??
			(normalizedFeatures.length === 1
				? normalizedFeatures[0]
				: null)
		);
	}

	private normalizeEngineeringFeaturesResponse(
		response: any,
		litheId: string
	): TLitheEngineeringFeaturesResponse {
		return {
			litheId:
				response?.litheId ?? response?.lithe_id ?? litheId,
			sectionTitle:
				response?.sectionTitle ??
				response?.section_title ??
				"Engineering Features",
			sectionDescription:
				response?.sectionDescription ??
				response?.section_description ??
				"Define custom engineered features with SQL code",
			cardTitle:
				response?.cardTitle ??
				response?.card_title ??
				"Define custom engineered features with SQL code",
			features:
				this.getNormalizedEngineeringFeatures(response),
		};
	}

	private normalizeEngineeringFeaturePreviewResponse(
		response: any,
		litheId: string,
		request: TLitheEngineeringFeaturePreviewRequest
	): TLitheEngineeringFeaturePreviewResponse {
		const previewRows = this.buildPreviewRowsFromValues(
			response?.previewRows ??
				response?.preview_rows ??
				response?.preview_values,
			request.featureName || "value"
		);
		const isSuccess =
			response?.isSuccess ??
			response?.is_success ??
			!response?.error_message;

		return {
			isSuccess,
			message:
				response?.message ??
				response?.error_message ??
				(isSuccess
					? previewRows.length > 0
						? "Preview generated"
						: "Preview completed with no sample rows. You can still save this feature."
					: "Preview generation failed"),
			litheId:
				response?.litheId ?? response?.lithe_id ?? litheId,
			featureId:
				response?.featureId ??
				response?.col_id ??
				request.colId,
			featureName:
				response?.featureName ??
				response?.feature_name ??
				request.featureName,
			formula: response?.formula ?? request.formula,
			userInputType:
				response?.user_input_type ?? request.userInputType,
			previewRows,
		};
	}

	private serializeEngineeringFeature(
		feature: TLitheEngineeringFeature
	): Record<string, any> {
		return {
			col_id: feature.id,
			name: feature.featureName,
			description: feature.description,
			datatype: feature.datatype,
			user_input: feature.formula,
			user_input_type: "sql_code",
			is_user_confirmed: feature.isApproved ?? false,
		};
	}

	private async getCurrentCustomColumns(
		litheId: string
	): Promise<Array<Record<string, any>>> {
		const url = `${LitheService.base_url}/${litheId}/custom_columns/questions`;
		const { data } = await LitheService.http.get<any>(url);

		return this.getNormalizedEngineeringFeatures(data).map(
			(feature) => ({
				col_id: feature.id,
				datatype: feature.datatype,
			})
		);
	}

	async getAllLithes(): Promise<any> {
		const url = `${LitheService.base_url}`;
		const { data } = await LitheService.http.get<any>(url);
		return data;
	}

	async litheSearch(
		request: TTapeSearchParameters,
		config?: AxiosRequestConfig
	): Promise<TLitheSearchResponseProps> {
		const url = `${LitheService.base_url}/search?page=${request.page}`;

		try {
			const { data } =
				await LitheService.http.post<TLitheSearchResponseProps>(
					url,
					{
						data: request.form,
						...config,
					}
				);
			return data;
		} catch (error: any) {
			return {
				lithes: [],
				count: 0,
				error: error.response,
			};
		}
	}

	async createLithe(
		litheCreationData: TLitheCreationData
	): Promise<any> {
		const creationType =
			litheCreationData.litheCreationType ===
			LitheCreationTypeEnum.DATASET
				? LitheCreationTypeEnum.UG
				: litheCreationData.litheCreationType;
		const url = `${LitheService.base_url}`;
		const { data } = await LitheService.http.post<any>(
			url,
			{
				data: {
					lithe_name: litheCreationData.litheName,
					asset_class_id: litheCreationData.assetClassId,
					lithe_creation_type: creationType,
					lithe_scope: litheCreationData.litheScope,
				},
			}
		);
		return data;
	}

	async getLithe(litheId: string): Promise<any> {
		const url = `${LitheService.base_url}/${litheId}/meta`;
		const { data } = await LitheService.http.get<any>(url);
		return data;
	}

	async getLitheSteps(litheId: string): Promise<any> {
		const url = `${LitheService.base_url}/${litheId}/steps`;
		const { data } = await LitheService.http.get<any>(url);
		return data;
	}

	async getLitheCleanDataResult(
		litheId: string
	): Promise<any> {
		const url = `${LitheService.base_url}/${litheId}/clean_data`;
		const { data } = await LitheService.http.get<any>(url);
		return data;
	}

	async processLitheCleanData(
		litheId: string,
		isStepConfirmed = false
	): Promise<any> {
		const url = `${LitheService.base_url}/${litheId}/clean_data`;
		const { data } = await LitheService.http.post<any>(
			url,
			{
				data: {
					is_step_confirmed: isStepConfirmed,
				},
			}
		);
		return data;
	}

	async getLitheFiltersResult(
		litheId: string
	): Promise<any> {
		const url = `${LitheService.base_url}/${litheId}/filters`;
		const { data } = await LitheService.http.get<any>(url);
		return this.logApiResponse("GET /filters", data);
	}

	async processLitheFilters(
		litheId: string,
		isStepConfirmed = false
	): Promise<any> {
		return await this.postStepConfirmation(
			litheId,
			"filters",
			isStepConfirmed
		);
	}

	async getLitheTransDefResult(
		litheId: string
	): Promise<any> {
		const url = `${LitheService.base_url}/${litheId}/trans_def`;
		const { data } = await LitheService.http.get<any>(url);
		return this.logApiResponse("GET /trans_def", data);
	}

	async processLitheTransDef(
		litheId: string,
		isStepConfirmed = false
	): Promise<any> {
		return await this.postStepConfirmation(
			litheId,
			"trans_def",
			isStepConfirmed
		);
	}

	async getLitheUgFeatureEngineeringResult(
		litheId: string
	): Promise<TLitheUgFeatureEngineeringResponse> {
		return await this.getStepResult(
			litheId,
			"ug_feature_engg"
		);
	}

	async processLitheUgFeatureEngineering(
		litheId: string,
		isStepConfirmed = false
	): Promise<TLitheUgFeatureEngineeringResponse> {
		return await this.postStepConfirmation(
			litheId,
			"ug_feature_engg",
			isStepConfirmed
		);
	}

	async getLitheUgReplinesResult(
		litheId: string
	): Promise<any> {
		const url = `${LitheService.base_url}/${litheId}/ug_replines`;
		const { data } = await LitheService.http.get<any>(url);
		return this.logApiResponse("GET /ug_replines", data);
	}

	async processLitheUgReplines(
		litheId: string,
		isStepConfirmed = false
	): Promise<any> {
		return await this.postStepConfirmation(
			litheId,
			"ug_replines",
			isStepConfirmed
		);
	}

	async getLitheEngineeringFeatures(
		litheId: string
	): Promise<TLitheEngineeringFeaturesResponse> {
		const url = `${LitheService.base_url}/${litheId}/custom_columns/questions`;
		const { data } = await LitheService.http.get<any>(url);

		return this.normalizeEngineeringFeaturesResponse(
			data,
			litheId
		);
	}

	buildLitheEngineeringFeaturesResponse(
		litheId: string,
		response: any
	): TLitheEngineeringFeaturesResponse {
		return this.normalizeEngineeringFeaturesResponse(
			response,
			litheId
		);
	}

	async updateLitheEngineeringFeatures(
		litheId: string,
		features: Array<TLitheEngineeringFeature>
	): Promise<TLitheEngineeringFeaturesResponse> {
		const existingColumns =
			await this.getCurrentCustomColumns(litheId);
		const existingIds = new Set(
			existingColumns.map((column) => String(column.col_id))
		);
		const nextIds = new Set(
			features.map((feature) => String(feature.id))
		);

		await Promise.all(
			features.map(async (feature) => {
				const existingColumn = existingColumns.find(
					(column) =>
						String(column.col_id) === String(feature.id)
				);
				const url = `${LitheService.base_url}/${litheId}/custom_columns/questions`;
				const customColumnFeedback =
					this.serializeEngineeringFeature(feature);

				if (!feature.datatype && existingColumn?.datatype) {
					customColumnFeedback.datatype =
						existingColumn.datatype;
				}

				if (!existingColumn) {
					delete customColumnFeedback.col_id;
				}

				await LitheService.http.post<any>(url, {
					data: {
						custom_column_feedback: customColumnFeedback,
					},
				});
			})
		);

		await Promise.all(
			existingColumns
				.filter(
					(column) => !nextIds.has(String(column.col_id))
				)
				.map(async (column) => {
					const colId = String(column.col_id);
					if (!existingIds.has(colId)) return;

					const url = `${LitheService.base_url}/${litheId}/custom_columns/questions/${colId}`;
					await LitheService.http.delete<any>(url);
				})
		);

		return await this.getLitheEngineeringFeatures(litheId);
	}

	async upsertLitheEngineeringFeature(
		litheId: string,
		feature: TLitheEngineeringFeature
	): Promise<TLitheEngineeringFeature> {
		const url = `${LitheService.base_url}/${litheId}/custom_columns/questions`;
		const customColumnFeedback =
			this.serializeEngineeringFeature(feature);

		if (!feature.id) {
			delete customColumnFeedback.col_id;
		}

		const { data } = await LitheService.http.post<any>(
			url,
			{
				data: {
					custom_column_feedback: customColumnFeedback,
				},
			}
		);
		const matchedFeature =
			this.findEngineeringFeatureInResponse(data, {
				featureId: feature.id,
				featureName: feature.featureName,
			});

		if (!matchedFeature) {
			throw new Error(
				"Saved engineering feature was not returned by the API."
			);
		}

		return matchedFeature;
	}

	async previewLitheEngineeringFeature(
		litheId: string,
		request: TLitheEngineeringFeaturePreviewRequest
	): Promise<TLitheEngineeringFeaturePreviewResponse> {
		const url = `${LitheService.base_url}/${litheId}/custom_columns/questions/${request.colId}/preview`;
		const { data } = await LitheService.http.post<any>(
			url,
			{
				data: {
					custom_column_feedback: {
						user_input: request.formula,
						user_input_type: request.userInputType,
					},
				},
			}
		);

		return this.normalizeEngineeringFeaturePreviewResponse(
			data,
			litheId,
			request
		);
	}

	async getProxyDataQuestions(
		litheId: string
	): Promise<any> {
		return await this.getStepQuestions(
			litheId,
			"proxy_data"
		);
	}

	async updateProxyDataQuestion(
		requestObject: TLitheQuestionRequestObject
	): Promise<any> {
		return await this.updateStepQuestion(
			"proxy_data",
			requestObject
		);
	}

	async confirmProxyData(litheId: string): Promise<any> {
		return await this.confirmStep(litheId, "proxy_data");
	}

	async getProxyDataCustomFilters(
		litheId: string
	): Promise<any> {
		const url = `${LitheService.base_url}/${litheId}/proxy_data/custom_filters`;
		const { data } = await LitheService.http.get<any>(url);
		return this.logApiResponse(
			"GET /proxy_data/custom_filters",
			data
		);
	}

	async createProxyDataFilterCustomFilter(
		litheId: string,
		payload: {
			filter_name: string;
			code: string;
			code_type?: "sql";
		}
	): Promise<any> {
		const url = `${LitheService.base_url}/${litheId}/proxy_data/custom_filters`;
		const { data } = await LitheService.http.post<any>(
			url,
			{
				data: payload,
			}
		);
		return this.logApiResponse(
			"POST /proxy_data/custom_filters",
			data
		);
	}

	async updateProxyDataFilterCustomFilter(
		litheId: string,
		filterId: string,
		payload: {
			filter_name: string;
			code: string;
			code_type?: "sql";
		}
	): Promise<any> {
		const encodedFilterId = encodeURIComponent(filterId);
		const url = `${LitheService.base_url}/${litheId}/proxy_data/custom_filters/${encodedFilterId}`;
		const { data } = await LitheService.http.put<any>(url, {
			data: payload,
		});
		return this.logApiResponse(
			"PUT /proxy_data/custom_filters/:id",
			data
		);
	}

	async deleteProxyDataFilterCustomFilter(
		litheId: string,
		filterId: string
	): Promise<any> {
		const encodedFilterId = encodeURIComponent(filterId);
		const url = `${LitheService.base_url}/${litheId}/proxy_data/custom_filters/${encodedFilterId}`;
		const { data } =
			await LitheService.http.delete<any>(url);
		return this.logApiResponse(
			"DELETE /proxy_data/custom_filters/:id",
			data
		);
	}

	async getProxyDataResults(litheId: string): Promise<any> {
		const url = `${LitheService.base_url}/${litheId}/proxy_data`;
		const { data } = await LitheService.http.get<any>(url);
		return this.logApiResponse("GET /proxy_data", data);
	}

	async processProxyDataAnalysis(
		litheId: string
	): Promise<any> {
		const url = `${LitheService.base_url}/${litheId}/proxy_data`;
		const { data } = await LitheService.http.post<any>(
			url,
			{
				data: {
					is_step_confirmed: false,
				},
			}
		);
		return this.logApiResponse("POST /proxy_data", data);
	}

	async getCustomColumnsResults(
		litheId: string
	): Promise<any> {
		const url = `${LitheService.base_url}/${litheId}/custom_columns`;
		const { data } = await LitheService.http.get<any>(url);
		return this.logApiResponse("GET /custom_columns", data);
	}

	async processCustomColumnsAnalysis(
		litheId: string
	): Promise<any> {
		const url = `${LitheService.base_url}/${litheId}/custom_columns`;
		const { data } = await LitheService.http.post<any>(
			url,
			{
				data: {
					is_step_confirmed: false,
				},
			}
		);
		return this.logApiResponse(
			"POST /custom_columns",
			data
		);
	}

	async confirmCustomColumns(
		litheId: string
	): Promise<any> {
		const url = `${LitheService.base_url}/${litheId}/custom_columns`;
		const { data } = await LitheService.http.post<any>(
			url,
			{
				data: {
					is_step_confirmed: true,
				},
			}
		);
		return this.logApiResponse(
			"POST /custom_columns",
			data
		);
	}

	async getTrainingDataQuestions(
		litheId: string
	): Promise<any> {
		return await this.getStepQuestions(
			litheId,
			"training_data"
		);
	}

	async updateTrainingDataQuestion(
		requestObject: TLitheQuestionRequestObject
	): Promise<any> {
		return await this.updateStepQuestion(
			"training_data",
			requestObject
		);
	}

	async confirmTrainingData(litheId: string): Promise<any> {
		return await this.confirmStep(litheId, "training_data");
	}

	async getTrainingDataCustomFilters(
		litheId: string
	): Promise<any> {
		const url = `${LitheService.base_url}/${litheId}/training_data/custom_filters`;
		const { data } = await LitheService.http.get<any>(url);
		return this.logApiResponse(
			"GET /training_data/custom_filters",
			data
		);
	}

	async createTrainingDataCustomFilter(
		litheId: string,
		payload: {
			filter_name: string;
			code: string;
			code_type?: "sql";
		}
	): Promise<any> {
		const url = `${LitheService.base_url}/${litheId}/training_data/custom_filters`;
		const { data } = await LitheService.http.post<any>(
			url,
			{
				data: payload,
			}
		);
		return this.logApiResponse(
			"POST /training_data/custom_filters",
			data
		);
	}

	async updateTrainingDataCustomFilter(
		litheId: string,
		filterName: string,
		payload: {
			filter_name: string;
			code: string;
			code_type?: "sql";
		}
	): Promise<any> {
		const encodedFilterId = encodeURIComponent(filterName);
		const url = `${LitheService.base_url}/${litheId}/training_data/custom_filters/${encodedFilterId}`;
		const { data } = await LitheService.http.put<any>(url, {
			data: payload,
		});
		return this.logApiResponse(
			"PUT /training_data/custom_filters/:id",
			data
		);
	}

	async deleteTrainingDataCustomFilter(
		litheId: string,
		filterName: string
	): Promise<any> {
		const encodedFilterId = encodeURIComponent(filterName);
		const url = `${LitheService.base_url}/${litheId}/training_data/custom_filters/${encodedFilterId}`;
		const { data } =
			await LitheService.http.delete<any>(url);
		return this.logApiResponse(
			"DELETE /training_data/custom_filters/:id",
			data
		);
	}

	async getTrainingDataResults(
		litheId: string
	): Promise<any> {
		const url = `${LitheService.base_url}/${litheId}/training_data`;
		const { data } = await LitheService.http.get<any>(url);
		return this.logApiResponse("GET /training_data", data);
	}

	async processTrainingDataAnalysis(
		litheId: string
	): Promise<any> {
		const url = `${LitheService.base_url}/${litheId}/training_data`;
		const { data } = await LitheService.http.post<any>(
			url,
			{
				data: {
					is_step_confirmed: false,
				},
			}
		);
		return this.logApiResponse("POST /training_data", data);
	}

	async getTransitionConfigQuestions(
		litheId: string
	): Promise<any> {
		return await this.getStepQuestions(
			litheId,
			"transition_config"
		);
	}

	async updateTransitionConfigQuestion(
		requestObject: TLitheQuestionRequestObject
	): Promise<any> {
		return await this.updateStepQuestion(
			"transition_config",
			requestObject
		);
	}

	async confirmTransitionConfig(
		litheId: string
	): Promise<any> {
		return await this.confirmStep(
			litheId,
			"transition_config"
		);
	}

	async getTransitionConfigResults(
		litheId: string
	): Promise<any> {
		const url = `${LitheService.base_url}/${litheId}/transition_config`;
		const { data } = await LitheService.http.get<any>(url);
		return this.logApiResponse(
			"GET /transition_config",
			data
		);
	}

	async processTransitionConfigAnalysis(
		litheId: string
	): Promise<any> {
		const url = `${LitheService.base_url}/${litheId}/transition_config`;
		const { data } = await LitheService.http.post<any>(
			url,
			{
				data: {
					is_step_confirmed: false,
				},
			}
		);
		return this.logApiResponse(
			"POST /transition_config",
			data
		);
	}

	async getReplinesQuestions(
		litheId: string
	): Promise<any> {
		const url = `${LitheService.base_url}/${litheId}/replines/questions`;
		const { data } = await LitheService.http.get<any>(url);
		return this.logApiResponse(
			"GET /replines/questions",
			data
		);
	}

	async updateReplinesQuestions(
		litheId: string,
		payload: Record<string, any>
	): Promise<any> {
		const url = `${LitheService.base_url}/${litheId}/replines/questions`;
		const { data } = await LitheService.http.post<any>(
			url,
			{
				data: payload,
			}
		);
		return this.logApiResponse(
			"POST /replines/questions",
			data
		);
	}

	async getReplinesResults(litheId: string): Promise<any> {
		const url = `${LitheService.base_url}/${litheId}/replines`;
		const { data } = await LitheService.http.get<any>(url);
		return this.logApiResponse("GET /replines", data);
	}

	async processReplinesAnalysis(
		litheId: string
	): Promise<any> {
		const url = `${LitheService.base_url}/${litheId}/replines`;
		const { data } = await LitheService.http.post<any>(
			url,
			{
				data: {
					is_step_confirmed: false,
				},
			}
		);
		return this.logApiResponse("POST /replines", data);
	}

	async confirmReplines(litheId: string): Promise<any> {
		return await this.confirmStep(litheId, "replines");
	}

	async getLitheSpecsQuestions(
		litheId: string
	): Promise<any> {
		const url = `${LitheService.base_url}/${litheId}/specs/questions`;
		const { data } = await LitheService.http.get<any>(url);
		return this.logApiResponse(
			"GET /specs/questions",
			data
		);
	}

	async updateLitheSpecsQuestions(
		requestObject: TLitheQuestionRequestObject
	): Promise<any> {
		const url = `${LitheService.base_url}/${requestObject.litheId}/specs/questions`;
		const { data } = await LitheService.http.post<any>(
			url,
			{
				data: this.buildQuestionPayload(requestObject),
			}
		);
		return this.logApiResponse(
			"POST /specs/questions",
			data
		);
	}

	async confirmSpecs(litheId: string): Promise<any> {
		const url = `${LitheService.base_url}/${litheId}/specs`;
		const { data } = await LitheService.http.post<any>(
			url,
			{
				data: {
					is_step_confirmed: true,
				},
			}
		);
		return this.logApiResponse("POST /specs", data);
	}

	async getLitheDataVizQuestions(
		litheId: string
	): Promise<any> {
		const url = `${LitheService.base_url}/${litheId}/data_viz/questions`;
		const { data } = await LitheService.http.get<any>(url);
		return data;
	}

	async getLitheDataVizResults(
		litheId: string,
		config?: AxiosRequestConfig
	): Promise<any> {
		const url = `${LitheService.base_url}/${litheId}/data_viz`;

		try {
			const { data } = await LitheService.http.get<any>(
				url,
				{
					signal: config?.signal,
				}
			);
			return data;
		} catch (error: any) {
			throw error.response;
		}
	}

	async updateLitheDataVizQuestions(
		requestObject: TLitheQuestionRequestObject
	): Promise<any> {
		const litheId = requestObject.litheId;
		const url = `${LitheService.base_url}/${litheId}/data_viz/questions`;
		const { data } = await LitheService.http.post<any>(
			url,
			{
				data: {
					question_id: requestObject.questionId,
					question_category_id:
						requestObject.questionCategoryId,
					user_input: requestObject.userInput,
					user_input_type: requestObject.userInputType,
				},
			}
		);
		return data;
	}

	async processDataViz(litheId: string): Promise<any> {
		const url = `${LitheService.base_url}/${litheId}/data_viz`;
		const { data } = await LitheService.http.post<any>(
			url,
			{
				data: {
					is_step_confirmed: false,
				},
			}
		);
		return data;
	}

	async confirmDataViz(litheId: string): Promise<any> {
		const url = `${LitheService.base_url}/${litheId}/data_viz`;
		const { data } = await LitheService.http.post<any>(
			url,
			{
				data: {
					is_step_confirmed: true,
				},
			}
		);
		return data;
	}

	async getLitheCashflowVizQuestions(
		litheId: string
	): Promise<any> {
		const url = `${LitheService.base_url}/${litheId}/cashflow_viz/questions`;
		const { data } = await LitheService.http.get<any>(url);
		return data;
	}

	async getLitheCashflowVizResults(
		litheId: string,
		config?: AxiosRequestConfig
	): Promise<any> {
		const url = `${LitheService.base_url}/${litheId}/cashflow_viz`;

		try {
			const { data } = await LitheService.http.get<any>(
				url,
				{
					signal: config?.signal,
				}
			);
			return data;
		} catch (error: any) {
			throw error.response;
		}
	}

	async updateLitheCashflowVizQuestions(
		requestObject: TLitheQuestionRequestObject
	): Promise<any> {
		const litheId = requestObject.litheId;
		const url = `${LitheService.base_url}/${litheId}/cashflow_viz/questions`;
		const { data } = await LitheService.http.post<any>(
			url,
			{
				data: {
					question_id: requestObject.questionId,
					question_category_id:
						requestObject.questionCategoryId,
					user_input: requestObject.userInput,
					user_input_type: requestObject.userInputType,
				},
			}
		);
		return data;
	}

	async processCashflowViz(litheId: string): Promise<any> {
		const url = `${LitheService.base_url}/${litheId}/cashflow_viz`;
		const { data } = await LitheService.http.post<any>(
			url,
			{
				data: {
					is_step_confirmed: false,
				},
			}
		);
		return data;
	}

	async confirmCashflowViz(litheId: string): Promise<any> {
		const url = `${LitheService.base_url}/${litheId}/cashflow_viz`;
		const { data } = await LitheService.http.post<any>(
			url,
			{
				data: {
					is_step_confirmed: true,
				},
			}
		);
		return data;
	}

	async getLitheProbsQuestions(
		litheId: string
	): Promise<any> {
		const url = `${LitheService.base_url}/${litheId}/probs/questions`;
		const { data } = await LitheService.http.get<any>(url);
		return data;
	}

	async updateLitheProbsQuestions(
		requestObject: TLitheQuestionRequestObject
	): Promise<any> {
		const litheId = requestObject.litheId;
		const url = `${LitheService.base_url}/${litheId}/probs/questions`;
		const { data } = await LitheService.http.post<any>(
			url,
			{
				data: {
					question_id: requestObject.questionId,
					question_category_id:
						requestObject.questionCategoryId,
					user_input: requestObject.userInput,
					user_input_type: requestObject.userInputType,
				},
			}
		);
		return data;
	}

	async getLitheProbsResults(
		litheId: string,
		config?: AxiosRequestConfig
	): Promise<any> {
		const url = `${LitheService.base_url}/${litheId}/probs`;

		try {
			const { data } = await LitheService.http.get<any>(
				url,
				{
					signal: config?.signal,
				}
			);
			return data;
		} catch (error: any) {
			throw error.response;
		}
	}

	async processProbs(litheId: string): Promise<any> {
		const url = `${LitheService.base_url}/${litheId}/probs`;
		const { data } = await LitheService.http.post<any>(
			url,
			{
				data: {
					is_step_confirmed: false,
				},
			}
		);
		return data;
	}

	async confirmProbs(litheId: string): Promise<any> {
		const url = `${LitheService.base_url}/${litheId}/probs`;
		const { data } = await LitheService.http.post<any>(
			url,
			{
				data: {
					is_step_confirmed: true,
				},
			}
		);
		return data;
	}

	async getLitheManualRFQuestions(
		litheId: string
	): Promise<any> {
		return await this.getStepQuestions(
			litheId,
			"manual_rf"
		);
	}

	async updateLitheManualRFQuestions(
		requestObject: TLitheQuestionRequestObject
	): Promise<any> {
		return await this.updateStepQuestion(
			"manual_rf",
			requestObject
		);
	}

	async getLitheManualRF(litheId: string): Promise<any> {
		return await this.getStepResult(litheId, "manual_rf");
	}

	async processLitheManualRF(
		litheId: string,
		isStepConfirmed: boolean
	): Promise<any> {
		return await this.postStepConfirmation(
			litheId,
			"manual_rf",
			isStepConfirmed
		);
	}

	async getLitheRFSpecsQuestions(
		litheId: string
	): Promise<any> {
		return await this.getStepQuestions(litheId, "rf_specs");
	}

	async updateLitheRFSpecsQuestions(
		requestObject: TLitheQuestionRequestObject
	): Promise<any> {
		return await this.updateStepQuestion(
			"rf_specs",
			requestObject
		);
	}

	async getLitheRFSpecsResults(
		litheId: string
	): Promise<any> {
		return await this.getStepResult(litheId, "rf_specs");
	}

	async processLitheRFSpecsAnalysis(
		litheId: string
	): Promise<any> {
		return await this.postStepConfirmation(
			litheId,
			"rf_specs",
			false
		);
	}

	async getLitheApprovalInfo(
		litheId: string,
		isUserGuided = false
	): Promise<any> {
		const approvalStep = isUserGuided
			? "ug_approval"
			: "approval";
		const url = `${LitheService.base_url}/${litheId}/${approvalStep}`;
		const { data } = await LitheService.http.get<any>(url);
		return this.logApiResponse(
			`GET /${approvalStep}`,
			data
		);
	}

	async confirmLitheApprovalInfo(
		litheId: string,
		isUserGuided = false
	): Promise<any> {
		const approvalStep = isUserGuided
			? "ug_approval"
			: "approval";
		const url = `${LitheService.base_url}/${litheId}/${approvalStep}`;
		const { data } = await LitheService.http.post<any>(
			url,
			isUserGuided
				? { data: { is_step_confirmed: true } }
				: undefined
		);
		return this.logApiResponse(
			`POST /${approvalStep}`,
			data
		);
	}

	async getLitheUgRFSpecsResults(
		litheId: string
	): Promise<any> {
		return await this.getStepResult(litheId, "ug_rf_specs");
	}

	async processLitheUgRFSpecs(
		litheId: string,
		isStepConfirmed = false
	): Promise<any> {
		return await this.postStepConfirmation(
			litheId,
			"ug_rf_specs",
			isStepConfirmed
		);
	}

	async confirmLitheRFSpecs(litheId: string): Promise<any> {
		return await this.confirmStep(litheId, "rf_specs");
	}

	async getSourceColumns(
		litheId: string,
		forStep:
			| "specs"
			| "proxy_data"
			| "custom_columns"
			| "training_data"
			| "transition_config"
			| "replines"
			| "rf_specs"
			| "raw_probs"
			| "approval"
			| "lithe_approved",
		abortSignal?: AbortSignal
	): Promise<Array<string>> {
		const url = `${LitheService.base_url}/${litheId}/meta/source_columns`;
		const { data } =
			await LitheService.http.post<TLitheSourceColumnsResponse>(
				url,
				{
					data: {
						for_step: forStep,
					},
					signal: abortSignal,
				}
			);
		const rawColumns = Array.isArray(data.source_col_names)
			? data.source_col_names
			: Array.isArray(data.source_columns)
				? data.source_columns
				: [];
		const sourceColumns = rawColumns.filter(
			(column): column is string =>
				typeof column === "string" &&
				column.trim().length > 0
		);
		return this.logApiResponse(
			"POST /meta/source_columns",
			sourceColumns
		);
	}

	async getLitheRawProbData(litheId: string): Promise<any> {
		const url = `${LitheService.base_url}/${litheId}/raw_probs`;
		const { data } = await LitheService.http.get<any>(url);
		return data;
	}

	async updateLitheRawProbData(
		litheId: string,
		data: any
	): Promise<any> {
		const url = `${LitheService.base_url}/${litheId}/raw_probs/data`;
		const { data: response } =
			await LitheService.http.post<any>(url, {
				data,
			});
		return response;
	}

	async processRawProbs(
		litheId: string,
		isStepConfirmed = true
	): Promise<any> {
		const url = `${LitheService.base_url}/${litheId}/raw_probs`;
		const { data } = await LitheService.http.post<any>(
			url,
			{
				data: {
					is_step_confirmed: isStepConfirmed,
				},
			}
		);
		return data;
	}

	async checkForUniqueLitheName(
		litheName: string,
		abortSignal: AbortSignal
	): Promise<any> {
		const url = `${LitheService.base_url}/does_name_exist`;
		const config = {
			data: {
				lithe_name: litheName,
			},
			signal: abortSignal,
		};
		const { data: response } =
			await LitheService.http.post<any>(url, config);
		return response;
	}
}
